import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/hooks/useAuth";
import { useT } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileDown, Lock, X } from "lucide-react";
import { listManualsPublic, listManualsForUser, getManualSignedUrl } from "@/lib/admin-manuals.functions";
import { verifyPdfContentType, openPdfInNewTab } from "@/lib/pdf-open";
import { toast } from "sonner";

type SearchParams = {
  manual?: string;
  lang?: string;
  version?: number;
};

export const Route = createFileRoute("/ke-stazeni")({
  component: DownloadsPage,
  validateSearch: (s: Record<string, unknown>): SearchParams => ({
    manual: typeof s.manual === "string" ? s.manual : undefined,
    lang: typeof s.lang === "string" ? s.lang : undefined,
    version: s.version != null && !Number.isNaN(Number(s.version)) ? Number(s.version) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Ke stažení — Cerveza.International" },
      { name: "description", content: "Manuály a průvodce ke stažení ve všech jazycích." },
      { property: "og:title", content: "Ke stažení — Cerveza.International" },
      { property: "og:description", content: "Manuály a průvodce ke stažení." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

type Manual = {
  id: string;
  slug: string;
  title_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
  audience: "public" | "vakant_owners";
  role_scope?: string | null;
  sort_order: number;
  files: Array<{ lang: string; storage_path: string; file_name: string }>;
};

function pick(map: Record<string, string> | undefined, lang: string, fallback = ""): string {
  if (!map) return fallback;
  return map[lang] || map.en || map.cs || Object.values(map)[0] || fallback;
}

const ALL = "__all__";

function DownloadsPage() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const search = Route.useSearch();
  

  const listPub = useServerFn(listManualsPublic);
  const listAuth = useServerFn(listManualsForUser);
  const signedUrl = useServerFn(getManualSignedUrl);

  const { data, isLoading } = useQuery({
    queryKey: ["downloads", "manuals", user ? "auth" : "public"],
    queryFn: async () => (user ? listAuth() : listPub()),
  });

  const [downloading, setDownloading] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<string>(ALL);
  const [position, setPosition] = useState<string>(ALL);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const autoOpenedRef = useRef(false);

  const manuals = ((data ?? []) as unknown) as Manual[];

  const positionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of manuals) {
      const r = (m.role_scope || "").trim();
      if (r && r !== "any") set.add(r);
    }
    return Array.from(set).sort();
  }, [manuals]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return manuals.filter((m) => {
      if (level !== ALL && m.audience !== level) return false;
      if (position !== ALL) {
        const r = (m.role_scope || "any").trim();
        if (r !== position) return false;
      }
      if (q) {
        const hay = [
          m.slug,
          pick(m.title_i18n, lang, ""),
          pick(m.description_i18n, lang, ""),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [manuals, level, position, query, lang]);

  const publicOnes = useMemo(() => filtered.filter((m) => m.audience === "public"), [filtered]);
  const vakantOnes = useMemo(() => filtered.filter((m) => m.audience === "vakant_owners"), [filtered]);




  const download = async (m: Manual, overrideLang?: string) => {
    const wanted = overrideLang || lang;
    const file =
      m.files.find((f) => f.lang === wanted) ||
      m.files.find((f) => f.lang === lang) ||
      m.files.find((f) => f.lang === "en") ||
      m.files[0];
    if (!file) {
      toast.error(
        t("downloads.noFile") ||
          `Pro tento manuál není k dispozici žádný soubor ke stažení.`,
      );
      return;
    }
    setDownloading(m.id);
    const errCode = `DL-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    try {
      let url: string;
      if (m.audience === "public") {
        const { data: pub } = supabase.storage.from("manuals").getPublicUrl(file.storage_path);
        url = pub?.publicUrl ?? "";
        if (!url) {
          throw new Error("empty_public_url");
        }
      } else {
        const res = await signedUrl({ data: { storage_path: file.storage_path } });
        url = res?.url ?? "";
        if (!url) {
          throw new Error("empty_signed_url");
        }
      }

      // Preflight: pokud je URL nedostupná nebo vrací HTML (např. smazaný objekt),
      // ukaž chybu MÍSTO otevření prázdné karty. `HEAD` je levný a rychlý.
      const preflight = await verifyPdfContentType(url);
      if (!preflight.ok) {
        toast.error(
          `${t("downloads.unreachable") || "Soubor se nepodařilo stáhnout"} — ${file.file_name} (HTTP ${preflight.status || "?"}, kód ${errCode}).`,
          {
            description:
              t("downloads.unreachableHint") ||
              "Zkontrolujte připojení a zkuste to znovu. Pokud problém přetrvává, kontaktujte správce.",
          },
        );
        return;
      }
      if (!preflight.isPdf) {
        toast.warning(
          `${t("downloads.notPdf") || "Soubor nemá typ application/pdf"} — ${file.file_name} (${preflight.contentType ?? "neznámý"}).`,
          {
            description:
              t("downloads.notPdfHint") ||
              "Otevírá se, ale některé prohlížeče jej stáhnou místo zobrazení.",
          },
        );
      }

      openPdfInNewTab(url, file.file_name);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[downloads] download failed", { code: errCode, manual: m.id, file: file.file_name, error: msg });
      toast.error(
        `${t("downloads.failed") || "Stažení se nezdařilo"} — ${file.file_name} (kód ${errCode}).`,
        {
          description:
            msg === "empty_public_url" || msg === "empty_signed_url"
              ? t("downloads.noUrl") || "Nepodařilo se získat odkaz na soubor."
              : t("downloads.retryHint") || "Zkuste to prosím za chvíli znovu.",
          action: {
            label: t("common.retry") || "Zkusit znovu",
            onClick: () => void download(m, overrideLang),
          },
        },
      );
    } finally {
      setDownloading(null);
    }
  };


  // Deep-link: scroll to & highlight target manual, optionally auto-open in given lang.
  useEffect(() => {
    if (!search.manual || manuals.length === 0) return;
    const target = manuals.find((m) => m.id === search.manual || m.slug === search.manual);
    if (!target) return;
    setHighlightedId(target.id);
    const el = cardRefs.current[target.id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    if (!autoOpenedRef.current && search.lang) {
      autoOpenedRef.current = true;
      void download(target, search.lang);
    }
    const to = window.setTimeout(() => setHighlightedId(null), 4000);
    return () => window.clearTimeout(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.manual, search.lang, manuals.length]);

  const levelLabel = (v: string) =>
    v === "public"
      ? t("downloads.levelPublic") || "Veřejné"
      : v === "vakant_owners"
      ? t("downloads.vakantOnly")
      : t("downloads.levelAll") || "Všechny úrovně";

  const renderCard = (m: Manual) => {
    const isHi = highlightedId === m.id;
    const targetLang = search.manual && (m.id === search.manual || m.slug === search.manual) ? search.lang : undefined;
    const otherLangs = m.files.filter((f) => f.lang !== lang).map((f) => f.lang);
    return (
      <Card
        key={m.id}
        ref={(el) => { cardRefs.current[m.id] = el; }}
        className={isHi ? "ring-2 ring-brand-terracotta transition-shadow" : undefined}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            {pick(m.title_i18n, lang, m.slug)}
            {m.audience === "vakant_owners" && (
              <Badge variant="secondary" className="text-xs">
                <Lock className="w-3 h-3 mr-1" /> {t("downloads.vakantOnly")}
              </Badge>
            )}
            {m.role_scope && m.role_scope !== "any" && (
              <Badge variant="outline" className="text-xs">{m.role_scope}</Badge>
            )}
            {isHi && search.version != null && (
              <Badge className="text-xs">v{search.version}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {pick(m.description_i18n, lang) && (
            <p className="text-sm text-muted-foreground">{pick(m.description_i18n, lang)}</p>
          )}
          <div className="flex gap-2 flex-wrap items-center">
            <Button size="sm" onClick={() => download(m, targetLang)} disabled={downloading === m.id || m.files.length === 0}>
              {downloading === m.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileDown className="w-4 h-4 mr-1" />}
              {t("downloads.downloadPdf")} ({(targetLang || (m.files.find((f) => f.lang === lang) ? lang : m.files[0]?.lang ?? "?")).toUpperCase()})
            </Button>
            {otherLangs.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                {otherLangs.map((l) => (
                  <Button
                    key={l}
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => download(m, l)}
                    disabled={downloading === m.id}
                  >
                    {l.toUpperCase()}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const hasActiveFilter = query.trim() !== "" || level !== ALL || position !== ALL;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
      <header>
        <h1 className="font-serif text-3xl md:text-4xl">{t("downloads.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("downloads.subtitle")}</p>
      </header>

      {!isLoading && manuals.length > 0 && (
        <div className="grid gap-2 md:grid-cols-[1fr_auto_auto_auto] items-center">
          <Input
            placeholder={t("downloads.searchPlaceholder") || "Hledat manuál…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="md:w-48">
              <SelectValue placeholder={t("downloads.filterLevel") || "Úroveň"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{levelLabel(ALL)}</SelectItem>
              <SelectItem value="public">{levelLabel("public")}</SelectItem>
              <SelectItem value="vakant_owners">{levelLabel("vakant_owners")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={position} onValueChange={setPosition} disabled={positionOptions.length === 0}>
            <SelectTrigger className="md:w-48">
              <SelectValue placeholder={t("downloads.filterPosition") || "Pozice"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("downloads.positionAll") || "Všechny pozice"}</SelectItem>
              {positionOptions.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasActiveFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setQuery(""); setLevel(ALL); setPosition(ALL); }}
            >
              <X className="w-4 h-4 mr-1" /> {t("downloads.clearFilters") || "Zrušit filtry"}
            </Button>
          )}
        </div>
      )}

      {isLoading ? (
        <Loader2 className="w-6 h-6 animate-spin" />
      ) : (
        <>
          <section className="space-y-3">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {hasActiveFilter
                  ? (t("downloads.noMatch") || "Žádný manuál neodpovídá filtrům.")
                  : t("downloads.empty")}
              </p>
            ) : (
              publicOnes.map(renderCard)
            )}
          </section>

          {vakantOnes.length > 0 && (
            <section className="space-y-3">
              <h2 className="font-serif text-xl flex items-center gap-2">
                <Lock className="w-4 h-4" /> {t("downloads.vakantOnly")}
              </h2>
              {vakantOnes.map(renderCard)}
            </section>
          )}

          {!user && (
            <p className="text-sm text-muted-foreground">
              <Link to="/auth" className="underline">{t("downloads.signInToView")}</Link>
            </p>
          )}
        </>
      )}
    </div>
  );
}
